import {
  faCheck,
  faCheckCircle,
  faExclamationCircle,
  faUserCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import useForm from "../hooks/useForm";
import { Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";
import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";

export default function Profile({ user, userInfo }) {
  const {
    handleProfileUpdate,
    handleUpdatePassword,
    errors,
    profileUpdateMessage,
    passwordUpdateMessage,
  } = useForm(user);
  const [isLoading, setIsLoading] = useState(true);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const currentDate = new Date().toISOString().split("T")[0];
  const [userData, setUserData] = useState({
    firstName: userInfo?.firstName,
    lastName: userInfo?.lastName,
    email: userInfo?.email,
    dateOfBirth: userInfo?.dateOfBirth,
    gender: userInfo?.gender,
    phoneNumber: userInfo?.phoneNumber,
    shippingAddress: {
      address: userInfo?.shippingAddress?.address,
      city: userInfo?.shippingAddress?.city,
      state: userInfo?.shippingAddress?.state,
      pincode: userInfo?.shippingAddress?.pincode,
    },
  });

  const [updatePassword, setUpdatePassword] = useState({
    oldPassword: "",
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (
      name === "oldPassword" ||
      name === "password" ||
      name === "confirmPassword"
    ) {
      setUpdatePassword({
        ...updatePassword,
        [name]: value,
      });
    } else if (
      name === "address" ||
      name === "pincode" ||
      name === "state" ||
      name === "city"
    ) {
      setUserData((userData) => ({
        ...userData,
        shippingAddress: {
          ...userData.shippingAddress,
          [name]: value,
        },
      }));
    } else if (name === "phoneNumber") {
      if (/^$|^[+-]?\d+$/.test(value)) {
        console.log("working");
        setUserData({
          ...userData,
          [name]: value,
        });
      }
    } else {
      setUserData({
        ...userData,
        [name]: value,
      });
    }
  };

  // Protects the profile page from disappearing on refresh.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    setUserData(userInfo);
  }, [userInfo]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return user ? (
    <div className="flex flex-col md:flex-row mt-10 md:mt-20 md:ml-40">
      <div className="md:w-1/5 md:pr-16 md:border-r-2 flex flex-col items-center">
        <h1 className="text-3xl font-semibold">User Profile</h1>
        <div className="mt-5 md:mt-10 border-2 rounded-full w-24 md:w-48 md:h-48 flex justify-center items-center">
          <FontAwesomeIcon
            icon={faUserCircle}
            className="w-full h-full text-gray-400"
          />
        </div>
      </div>

      {/* Right Container */}
      <div className="md:w-2/5 md:ml-16 mt-10">
        <form
          className="flex flex-col"
          onSubmit={(e) => handleProfileUpdate(e, userData, "profile")}
        >
          <div className="flex flex-col justify-between space-y-5 mb-5">
            <div className="flex flex-col md:flex-row md:space-x-5 space-y-4 md:space-y-0">
              <div className="w-full flex flex-col">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="First Name"
                  value={userData.firstName}
                  onChange={handleChange}
                  className="border-2 border-gray-500 rounded-md py-2 px-2 outline-none"
                />
                {errors.firstName && (
                  <small className="text-red-500 mt-0">
                    {errors.firstName}
                  </small>
                )}
              </div>
              <div className="w-full flex flex-col">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last Name"
                  value={userData.lastName}
                  onChange={handleChange}
                  className="border-2 border-gray-500 rounded-md py-2 px-2 outline-none"
                />
                {errors.lastName && (
                  <small className="text-red-500 mt-0">{errors.lastName}</small>
                )}
              </div>
            </div>
            <div className="flex flex-col">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                name="email"
                placeholder="Email address"
                value={userData.email}
                disabled
                onChange={handleChange}
                className="w-full border-2 border-gray-500 rounded-md py-2 px-2 outline-none"
              />
              {errors.email && (
                <small className="text-red-500 mt-0">{errors.email}</small>
              )}
            </div>

            <div className="flex space-x-5 w-full">
              <div className="w-1/2 flex flex-col">
                <label htmlFor="dateOfBirth">Date of Birth</label>
                <div className="w-full flex items-center justify-between border-2 border-gray-500 rounded-md p-2 bg-white">
                  <input
                    type="text"
                    name="dateOfBirth"
                    placeholder="DOB DD/MM/YYYY"
                    value={userData.dateOfBirth}
                    onChange={handleChange}
                    className="w-full outline-none"
                    max={currentDate}
                    onFocus={(e) => (e.target.type = "date")}
                    onBlur={(e) => (e.target.type = "text")}
                  />
                </div>
                {errors.dob && (
                  <small className="text-red-500 mt-0">{errors.dob}</small>
                )}
              </div>
              <div className="w-1/2 flex flex-col">
                <label htmlFor="gender">Gender</label>
                <select
                  name="gender"
                  value={userData.gender}
                  onChange={handleChange}
                  className="w-full border-2 border-gray-500 rounded-md p-2 outline-none"
                >
                  {/* if value==null then show error required */}
                  <option value="">Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                {errors.gender && (
                  <small className="text-red-500 mt-0">{errors.gender}</small>
                )}
              </div>
            </div>
          </div>

          <h2 className="text-2xl my-5 pb-2 border-b-2">
            Shipping Information
          </h2>
          <div className="flex flex-col justify-between space-y-5 mb-5">
            <div className="flex flex-col">
              <label htmlFor="address">Address</label>
              <input
                type="text"
                name="address"
                placeholder="Address"
                value={userData?.shippingAddress?.address}
                onChange={handleChange}
                className="w-full border-2 border-gray-500 rounded-md py-2 px-2 outline-none"
              />
              {errors.address && (
                <small className="text-red-500 mt-0">{errors.address}</small>
              )}
            </div>

            <div className="flex space-x-5">
              {/* Get State API */}
              <div className="w-full flex flex-col">
                <label htmlFor="state">State</label>
                <input
                  type="text"
                  name="state"
                  placeholder="State"
                  value={userData?.shippingAddress?.state}
                  onChange={handleChange}
                  className="w-full border-2 border-gray-500 rounded-md py-2 px-2 outline-none"
                />
                {errors.state && (
                  <small className="text-red-500 mt-0">{errors.state}</small>
                )}
              </div>
              <div className="w-full flex flex-col">
                <label htmlFor="city">City</label>
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  value={userData?.shippingAddress?.city}
                  onChange={handleChange}
                  className="w-full border-2 border-gray-500 rounded-md py-2 px-2 outline-none"
                />
                {errors.city && (
                  <small className="text-red-500 mt-0">{errors.city}</small>
                )}
              </div>
              <div className="w-full flex flex-col">
                <label htmlFor="pincode">Pincode</label>
                <input
                  type="text"
                  name="pincode"
                  maxLength="6"
                  placeholder="Pincode"
                  value={userData?.shippingAddress?.pincode}
                  onChange={handleChange}
                  className="w-full border-2 border-gray-500 rounded-md py-2 px-2 outline-none"
                />
                {errors.pincode && (
                  <small className="text-red-500 mt-0">{errors.pincode}</small>
                )}
              </div>
            </div>
            <div className="flex flex-col">
              <label htmlFor="phoneNumber">Phone Number</label>
              <input
                type="text"
                name="phoneNumber"
                placeholder="Phone Number"
                disabled={userInfo?.phoneNumber !== null}
                value={userData.phoneNumber}
                onChange={handleChange}
                className="w-full border-2 border-gray-500 rounded-md py-2 px-2 outline-none"
              />
              {errors.phoneNumber && (
                <small className="text-red-500 mt-0">
                  {errors.phoneNumber}
                </small>
              )}
            </div>
          </div>
          <button
            className="md:w-2/5 w-full rounded-md p-2 md:p-1 text-lg bg-ambhosOrange text-white my-5 active:scale-95"
            type="submit"
          >
            Save Changes
          </button>
          {profileUpdateMessage && (
            <div className="mt-3 px-3 bg-green-300 flex items-center">
              <FontAwesomeIcon icon={faCheck} />
              <p className="pl-2 text-green-900">{profileUpdateMessage}</p>
            </div>
          )}
        </form>

        {/* Update Password */}
        {user?.providerData[0]?.providerId !== "google.com" && (
          <form
            onSubmit={(e) =>
              handleUpdatePassword(e, updatePassword, "updatePassword")
            }
          >
            <h2 className="text-2xl my-5 pb-2 border-b-2">Change Password</h2>
            <div className="flex flex-col justify-between space-y-5 mb-5">
              <div className="flex flex-col">
                <div className="flex items-center w-full border-2 border-gray-500 rounded-md">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    name="oldPassword"
                    placeholder="Current Password"
                    value={updatePassword.oldPassword}
                    onChange={handleChange}
                    className="w-full rounded-md py-2 px-2 outline-none"
                  />
                  {passwordVisible ? (
                    <FontAwesomeIcon
                      onClick={() => setPasswordVisible(false)}
                      className="text-lg px-2 cursor-pointer"
                      icon={faEyeSlash}
                    />
                  ) : (
                    <FontAwesomeIcon
                      onClick={() => setPasswordVisible(true)}
                      className="text-lg px-2 cursor-pointer"
                      icon={faEye}
                    />
                  )}
                </div>
                {errors.oldPassword && (
                  <small className="text-red-500 mt-0">
                    {errors.oldPassword}
                  </small>
                )}
              </div>
              <div className="flex flex-col">
                <input
                  type="password"
                  name="password"
                  placeholder="New Password"
                  value={updatePassword.password}
                  onChange={handleChange}
                  className="w-full border-2 border-gray-500 rounded-md py-2 px-2 outline-none"
                />
                {errors.password && (
                  <small className="text-red-500 mt-0">{errors.password}</small>
                )}
              </div>
              <div className="flex flex-col">
                <div className="flex items-center w-full border-2 border-gray-500 rounded-md">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Confirm New Password"
                    value={updatePassword.confirmPassword}
                    onChange={handleChange}
                    className="w-full rounded-md py-2 px-2 outline-none"
                  />
                  {passwordVisible ? (
                    <FontAwesomeIcon
                      onClick={() => setPasswordVisible(false)}
                      className="text-lg px-2 cursor-pointer"
                      icon={faEyeSlash}
                    />
                  ) : (
                    <FontAwesomeIcon
                      onClick={() => setPasswordVisible(true)}
                      className="text-lg px-2 cursor-pointer"
                      icon={faEye}
                    />
                  )}
                </div>
                {errors.confirmPassword && (
                  <small className="text-red-500 mt-0">
                    {errors.confirmPassword}
                  </small>
                )}
              </div>
            </div>
            <button
              className="md:w-2/5 w-full rounded-md p-2 md:p-1 text-lg bg-ambhosOrange text-white my-5"
              type="submit"
            >
              Change Password
            </button>
            {passwordUpdateMessage.successMessage && (
              <div className="mt-3 py-1 px-3 bg-green-200 flex items-center text-green-600">
                <FontAwesomeIcon className="" icon={faCheckCircle} />
                <p className="pl-2">{passwordUpdateMessage.successMessage}</p>
              </div>
            )}
            {passwordUpdateMessage.errorMessage && (
              <div className="mt-3 py-1 px-3 bg-red-200 flex items-center text-red-600">
                <FontAwesomeIcon className="" icon={faExclamationCircle} />
                <p className="pl-2">{passwordUpdateMessage.errorMessage}</p>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  ) : (
    <Navigate to="/store" />
  );
}
